import React, { useState, useEffect } from "react";
import { useLocation } from "wouter";

interface Firm {
  firmId: string;
  firmName: string;
}

interface UserFormData {
  firstName: string;
  lastName: string;
  email: string;
  paymentEnabled: boolean;
  firmId: string;
}

const CreateUserForm: React.FC = () => {
  const [formData, setFormData] = useState<UserFormData>({
    firstName: "",
    lastName: "",
    email: "",
    paymentEnabled: true,
    firmId: "",
  });

  const [firms, setFirms] = useState<Firm[]>([]);
  const [registrationCode, setRegistrationCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [, navigate] = useLocation();

  useEffect(() => {
    const fetchFirms = async () => {
      try {
        const res = await fetch("/fmlm/get-firms");
        if (!res.ok) throw new Error("Failed to fetch firms");
        const data = await res.json();
        setFirms(data);
      } catch (err) {
        console.error(err);
        setError("Could not load firms");
      }
    };
    fetchFirms();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    if (name === "paymentEnabled") {
      setFormData({
        ...formData,
        paymentEnabled: value === "true",
      });
    } else {
      setFormData({
        ...formData,
        [name]:
          type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setRegistrationCode(null);

    try {
      const response = await fetch("/fmlm/user", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error("Failed to create user");
      }

      const data = await response.json();
      if (data.registrationCode) {
        setRegistrationCode(data.registrationCode);
      }
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <div className="max-w-md mx-auto p-6 mt-[12rem]">
      <button
        onClick={() => navigate("/landing-page")}
        className="mb-4 text-[#4c7375] hover:underline"
      >
        ← Back
      </button>

      <h2 className="text-xl font-semibold mb-4">Create User</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          type="text"
          name="firstName"
          placeholder="First Name"
          value={formData.firstName}
          onChange={handleChange}
          className="w-full p-2 border rounded"
          required
        />

        <input
          type="text"
          name="lastName"
          placeholder="Last Name"
          value={formData.lastName}
          onChange={handleChange}
          className="w-full p-2 border rounded"
          required
        />

        <input
          type="email"
          name="email"
          placeholder="Email"
          value={formData.email}
          onChange={handleChange}
          className="w-full p-2 border rounded"
          required
        />

        {/* Firm Dropdown */}
        <select
          name="firmId"
          value={formData.firmId}
          onChange={handleChange}
          className="w-full p-2 border rounded"
          required
        >
          <option value="">Select Firm</option>
          {firms.map((firm) => (
            <option key={firm.firmId} value={firm.firmId}>
              {firm.firmName}
            </option>
          ))}
        </select>

        <div>
          <label className="block mb-1 font-medium">Payment Enabled</label>
          <div className="flex space-x-4">
            <label>
              <input
                type="radio"
                name="paymentEnabled"
                value="true"
                checked={formData.paymentEnabled === true}
                onChange={handleChange}
              />
              <span className="ml-2">True</span>
            </label>
            <label>
              <input
                type="radio"
                name="paymentEnabled"
                value="false"
                checked={formData.paymentEnabled === false}
                onChange={handleChange}
              />
              <span className="ml-2">False</span>
            </label>
          </div>
        </div>

        <button
          type="submit"
          className="w-full bg-[#4c7375] text-white py-2 rounded hover:bg-blue-700"
        >
          Create User
        </button>
      </form>

      {registrationCode && (
        <p className="mt-4">
          Your registration code is <b>{registrationCode}</b>
        </p>
      )}

      {error && <p className="mt-2 text-red-600">Error: {error}</p>}
    </div>
  );
};

export default CreateUserForm;
